import JSURL from 'jsurl';
import equal from 'fast-deep-equal/es6';
import type { ListSearchOptions } from 'c-sdk';

/**
 * What a `FilterableEntityTable` keeps in the URL, under `?state=~(list~(<tag>~(...)))`: the same
 * JSURL blob `useUrlState` writes, so a page's own URL state (tabs, report type) and its tables'
 * live side by side. Only what differs from the table's defaults is kept, so an untouched table
 * leaves the URL alone. The search term is kept as typed (`q`), not as `search.*` params: those
 * are derived from the table's `textSearchColumns`.
 */
export type TableUrlState = {
    q?: string;
    filters?: ListSearchOptions['filters'];
    page?: number;
    perPage?: number;
    orderBy?: string;
    direction?: ListSearchOptions['direction'];
};

const rootKey = 'state';
const listKey = 'list';

const parseBlob = (search: string): Record<string, any> => {
    const raw = new URLSearchParams(search).get(rootKey);
    if (!raw) return {};
    const parsed = JSURL.tryParse(raw, {});
    return parsed != null && typeof parsed === 'object' ? parsed : {};
};

export const readTableUrlState = (search: string, tag: string): TableUrlState | undefined => {
    const state = parseBlob(search)[listKey]?.[tag];
    return state != null && typeof state === 'object' ? state : undefined;
};

/**
 * The query string with `tag`'s state replaced (or removed, when `state` is undefined). Other
 * tables, other `state` keys and any other query params are kept.
 */
export const writeTableUrlState = (
    search: string,
    tag: string,
    state: TableUrlState | undefined,
): string => {
    const params = new URLSearchParams(search);
    const { [listKey]: tables = {}, ...rest } = parseBlob(search);
    const { [tag]: _previous, ...otherTables } = tables;
    const nextTables = state ? { ...otherTables, [tag]: state } : otherTables;
    const blob = Object.keys(nextTables).length > 0 ? { ...rest, [listKey]: nextTables } : rest;

    params.delete(rootKey);
    // Written by hand rather than through URLSearchParams, which would percent-encode JSURL's
    // ~'() and leave a URL that no longer matches what useUrlState writes.
    const parts = [
        params.toString(),
        Object.keys(blob).length > 0 ? `${rootKey}=${JSURL.stringify(blob)}` : '',
    ].filter(part => part.length > 0);

    return parts.length > 0 ? `?${parts.join('&')}` : '';
};

const compactFilters = (filters: ListSearchOptions['filters'] | undefined) =>
    Object.entries(filters ?? {}).reduce<NonNullable<ListSearchOptions['filters']>>(
        (acc, [key, values]) =>
            Array.isArray(values) && values.length > 0 ? { ...acc, [key]: values } : acc,
        {},
    );

const searchTerm = (searchables: ListSearchOptions['searchables'] | undefined) => {
    const term = Object.values(searchables ?? {}).find(value => value != null && value !== '');
    return term == null ? undefined : String(term);
};

/** The URL state for a search payload: what differs from `defaults`, or undefined if nothing. */
export const toTableUrlState = (
    payload: ListSearchOptions,
    defaults: ListSearchOptions,
): TableUrlState | undefined => {
    const state: TableUrlState = {};
    const q = searchTerm(payload.searchables);
    const filters = compactFilters(payload.filters);

    if (q != null) state.q = q;
    if (!equal(filters, compactFilters(defaults.filters))) state.filters = filters;
    if (payload.page != null && payload.page !== (defaults.page ?? 1)) state.page = payload.page;
    if (payload.perPage != null && payload.perPage !== defaults.perPage)
        state.perPage = payload.perPage;
    if (payload.orderBy != null && payload.orderBy !== defaults.orderBy)
        state.orderBy = payload.orderBy;
    if (payload.direction != null && payload.direction !== defaults.direction)
        state.direction = payload.direction;

    return Object.keys(state).length > 0 ? state : undefined;
};

/** The search payload a URL state stands for: `defaults` with the URL's values on top. */
export const fromTableUrlState = (
    state: TableUrlState | undefined,
    defaults: ListSearchOptions,
    searchablesFor: (term: string) => ListSearchOptions['searchables'],
): ListSearchOptions => ({
    ...defaults,
    filters: state?.filters ?? defaults.filters ?? {},
    searchables: state?.q ? searchablesFor(state.q) : {},
    page: state?.page ?? defaults.page ?? 1,
    perPage: state?.perPage ?? defaults.perPage,
    orderBy: state?.orderBy ?? defaults.orderBy,
    direction: state?.direction ?? defaults.direction,
});

/**
 * Whether the search term differs. A search for a new term also drops back to page 1, so this is
 * how a keystroke is told apart from a filter, page or ordering change.
 */
export const searchTermChanged = (from: TableUrlState | undefined, to: TableUrlState | undefined) =>
    (from?.q ?? '') !== (to?.q ?? '');
